<template>
  <div data-page-name="immutable-storage">
    <section class="hero">
      <div class="wrapper">
        <div class="left">
          <span class="tag">{{ $t('Immutability') }}</span>
          <h1 v-html="$t('immutable-storage.hero.title')" />
          <p v-html="$t('immutable-storage.hero.description')" />

          <div class="buttons">
            <Buttons.Link :to="REQUEST_DEMO_URL">
              {{ $t('Request_a_Demo') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Link>

            <Buttons.Regular
              fill="outline"
              accent="orange"
              @click="setFormState('visible', true)"
            >
              {{ $t('Download_Whitepaper') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Regular>
          </div>
        </div>

        <div class="right">
          <NuxtImg
            src="/redesign/images/server/server-top-and-front.webp"
            alt="Object first server top and front view"
            sizes="xs:328px md:704px xl:592px"
            densities="x1 x2"
            format="webp"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <div class="purple-1">
      <div class="wrapper">
        <div class="content">
          <p v-html="$t('immutable-storage.purple-1.p-1')" />
          <p v-html="$t('immutable-storage.purple-1.p-2')" />
        </div>
      </div>
    </div>

    <div class="tabs">
      <div class="wrapper">
        <TabList :tabs="TABS" :activeTabIndex v-slot="{ tab, index, active }">
          <button :class="{ active }" @click="setActiveTabIndex(index)">
            {{ tab }}
          </button>
        </TabList>

        <ul class="contents">
          <template v-for="(content, index) of TABS_CONTENT" :key="index">
            <li
              v-if="activeTabIndex === index"
              v-html="content"
              class="tab-content"
            />
          </template>
        </ul>
      </div>
    </div>

    <section class="role-of-storage">
      <div class="wrapper">
        <h2 v-html="$t('immutable-storage.role-of-storage.title')" />
        <p v-html="$t('immutable-storage.role-of-storage.description')" />
        <h4 v-html="$t('immutable-storage.role-of-storage.cards.title')" />

        <ul class="cards">
          <li
            v-for="({ title, icon, description }, index) of CARDS"
            :key="index"
            class="card"
          >
            <strong v-html="title" />
            <component :is="icon" />
            <p v-html="description" />
          </li>
        </ul>
      </div>

      <Swiper
        :modules="[Pagination]"
        :pagination="{ clickable: true }"
        :slides-offset-before="sliderOffset"
        :slides-offset-after="sliderOffset"
        :breakpoints="{
          320: {
            slidesPerView: 'auto',
            spaceBetween: 8,
          },
        }"
      >
        <SwiperSlide
          v-for="({ title, icon, description }, index) of CARDS"
          :key="index"
          class="card"
        >
          <strong v-html="title" />
          <component :is="icon" />
          <p v-html="description" />
        </SwiperSlide>
      </Swiper>
    </section>

    <section class="purple-whitepaper bg-purple-gradient">
      <div class="wrapper">
        <div class="left">
          <h2 v-html="$t('Learn_3_Reasons_Object_First_Is_Best_For_Veeam')" />

          <Buttons.Regular
            fill="outline"
            accent="white"
            @click="setFormState('visible', true)"
          >
            {{ $t('Download_Whitepaper') }}
            <SvgIcon name="download" fill="#fff" />
          </Buttons.Regular>
        </div>

        <div class="right">
          <NuxtImg
            src="/redesign/images/whitepaper/3-reasons-ootbi-is-the-best-storage-for-veeam-whitepaper-shadow-large.webp"
            alt="A brief description of the benefits of a secure, simple and powerful data warehouse for Veeam users."
            sizes="xs: md:195px xl:260px"
            densities="x1 x2"
            format="webp"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section class="key-benefits">
      <div class="wrapper">
        <div class="intro">
          <h2 v-html="$t('immutable-storage.key-benefits.title')" />

          <p
            class="description"
            v-html="$t('immutable-storage.key-benefits.description-1')"
          />

          <p
            class="description"
            v-html="$t('immutable-storage.key-benefits.description-2')"
          />
        </div>

        <ul class="list">
          <li
            v-for="({ title, description }, index) of KEY_BENEFITS"
            :key="index"
            class="item"
          >
            <strong v-html="title" />
            <p v-html="description" />
          </li>
        </ul>
      </div>

      <Swiper
        :modules="[Pagination]"
        :pagination="{ clickable: true }"
        :slides-offset-before="sliderOffset"
        :slides-offset-after="sliderOffset"
        :breakpoints="{
          320: {
            slidesPerView: 'auto',
            spaceBetween: 8,
          },
        }"
      >
        <SwiperSlide
          v-for="({ title, description }, index) of KEY_BENEFITS"
          :key="index"
          class="card"
        >
          <strong v-html="title" />
          <p v-html="description" />
        </SwiperSlide>
      </Swiper>
    </section>

    <section class="best-immutable-storage">
      <div class="wrapper">
        <h2 v-html="$t('immutable-storage.immutable-storage.title')" />
        <p v-html="$t('immutable-storage.immutable-storage.description')" />

        <Buttons.Link :to="REQUEST_DEMO_URL" fill="solid" accent="orange">
          {{ $t('Request_a_Demo') }}
          <SvgIcon name="arrow-forward" />
        </Buttons.Link>

        <NuxtImg
          src="/redesign/images/server/server-perspective-without-front-panel.webp"
          alt="perspective view of the server with the front panel removed"
          sizes="xs:328px md:592px xl:592px"
          densities="x1 x2"
          format="webp"
          loading="lazy"
        />
      </div>
    </section>

    <div class="purple-2">
      <div class="wrapper">
        <div class="content">
          <p v-html="$t('immutable-storage.purple-2.p-1')" />
        </div>
      </div>
    </div>

    <section class="related-topics">
      <div class="wrapper">
        <h2>{{ $t('Related_topics') }}</h2>

        <ul class="list">
          <li v-for="post of relatedTopics" :key="post.slug" class="item">
            <ButtonLinkUnderlined
              :to="localePathWithSlash(getArticleLink(post))"
              target="_blank"
            >
              {{ post.data.title }}
            </ButtonLinkUnderlined>
          </li>
        </ul>
      </div>
    </section>

    <section class="faq">
      <div class="wrapper">
        <div class="left">
          <h2 v-html="$t('FAQ')" />
        </div>

        <div class="right">
          <AccordionList>
            <AccordionItem
              v-for="({ title, description }, index) of ACCORDION"
              :key="index"
            >
              <template #summary>
                <h5 v-html="title" />
              </template>

              <template #icon>
                <SvgIcon name="chevron-down" />
              </template>

              <p v-html="description" />
            </AccordionItem>
          </AccordionList>
        </div>
      </div>
    </section>

    <section class="purple-3">
      <div class="wrapper">
        <div class="content">
          <h3 v-html="$t('immutable-storage.purple-3.title')" />

          <Buttons.Link :to="REQUEST_DEMO_URL" fill="outline" accent="white">
            {{ $t(PATHS.REQUEST_DEMO.i18nKey) }}
            <SvgIcon name="arrow-forward" />
          </Buttons.Link>
        </div>
      </div>
    </section>

    <section class="additional-info">
      <h2>{{ $t('on-premises-object-storage.additional-info.title') }}</h2>
      <p v-html="$t('on-premises-object-storage.additional-info.content')" />
    </section>
  </div>

  <ClientOnly>
    <Teleport to="#overlay">
      <Transition name="scale-fade">
        <Forms.Wrapper
          v-if="formState.visible"
          can-close
          @close="setFormState('visible', false)"
        >
          <template #title>
            <transition name="fade" mode="out-in">
              <span v-if="formState.submitted">
                {{ $t('Download_Asset') }}
              </span>

              <span v-else>
                {{ $t('Provide_your_details_to_get_the_asset') }}
              </span>
            </transition>
          </template>

          <template #default>
            <transition name="fade" mode="out-in">
              <Modals.DownloadAsset v-if="formState.submitted" />

              <Forms.GetAsset
                v-else
                @loading="setLoading"
                @submitted="onSubmitted"
              />
            </transition>
          </template>
        </Forms.Wrapper>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
// swiper
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

// accordion
// https://github.com/sticker0ne/vue3-rich-accordion
import { AccordionList, AccordionItem } from 'vue3-rich-accordion';
import 'vue3-rich-accordion/accordion-library-styles.css';

// components
import Buttons from '~/components/redesign/Buttons';
import TabList from '~/components/redesign/TabList.vue';
import Modals from '~/components/redesign/Modals';
import Forms from '~/components/redesign/Forms';
import ButtonLinkUnderlined from '~/components/redesign/ButtonLinkUnderlined.vue';

// composable
import { useI18n } from '#i18n';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// utils
import get from 'lodash.get';
import { getArticleLink } from '~/utils/getArticleLink';
import buildUTM from '~/utils/buildUTM';

// constants
import { PATHS } from '~/constants/nav-items';

// stores
import useUIStore from '~/stores/ui';

// icons
import SvgImmutabilityColorfull from '~/assets/svg/immutability-colorfull.svg?component';
import SvgHeartColorfull from '~/assets/svg/heart-colorfull.svg?component';
import SvgChartColorfull from '~/assets/svg/chart-colorfull.svg?component';
import SvgScalabilityColorfull from '~/assets/svg/scalability-colorfull.svg?component';
import SvgOnPermToCloudColorfull from '~/assets/svg/on-perm-to-cloud-colorfull.svg?component';
import SvgUpToDateColorfull from '~/assets/svg/up-to-date-colorfull.svg?component';

defineOptions({
  components: {
    SvgImmutabilityColorfull,
    SvgHeartColorfull,
    SvgChartColorfull,
    SvgScalabilityColorfull,
    SvgOnPermToCloudColorfull,
    SvgUpToDateColorfull,
  },
});

const { setLoading, setOverlay } = useUIStore();

const { t } = useI18n();
const localePathWithSlash = useLocalePathWithSlash();

const REQUEST_DEMO_URL =
  localePathWithSlash(PATHS.REQUEST_DEMO.path) +
  buildUTM({
    source: 'guide',
    campaign: 'custom-guide',
    content: 'immutable-storage',
  });

const formState = reactive({ visible: false, submitted: false });
const setFormState = <K extends keyof typeof formState>(
  key: K,
  value: (typeof formState)[K],
) => {
  formState[key] = value;
};

watchEffect(() => {
  setOverlay({ visible: formState.visible });
});

const onSubmitted = () => {
  setLoading(false);
  setFormState('submitted', true);
};

const TABS = [
  t('immutable-storage.tabs-1.name'),
  t('immutable-storage.tabs-2.name'),
  t('immutable-storage.tabs-3.name'),
];
const TABS_CONTENT = [
  t('immutable-storage.tabs-1.content'),
  t('immutable-storage.tabs-2.content'),
  t('immutable-storage.tabs-3.content'),
];
const CARDS = [
  {
    title: t('immutable-storage.role-of-storage.cards-1.title'),
    icon: 'SvgImmutabilityColorfull',
    description: t('immutable-storage.role-of-storage.cards-1.description'),
  },
  {
    title: t('immutable-storage.role-of-storage.cards-2.title'),
    icon: 'SvgHeartColorfull',
    description: t('immutable-storage.role-of-storage.cards-2.description'),
  },
  {
    title: t('immutable-storage.role-of-storage.cards-3.title'),
    icon: 'SvgChartColorfull',
    description: t('immutable-storage.role-of-storage.cards-3.description'),
  },
  {
    title: t('immutable-storage.role-of-storage.cards-4.title'),
    icon: 'SvgScalabilityColorfull',
    description: t('immutable-storage.role-of-storage.cards-4.description'),
  },
  {
    title: t('immutable-storage.role-of-storage.cards-5.title'),
    icon: 'SvgOnPermToCloudColorfull',
    description: t('immutable-storage.role-of-storage.cards-5.description'),
  },
  {
    title: t('immutable-storage.role-of-storage.cards-6.title'),
    icon: 'SvgUpToDateColorfull',
    description: t('immutable-storage.role-of-storage.cards-6.description'),
  },
];
const KEY_BENEFITS = [
  {
    title: t('immutable-storage.key-benefits.item-1.title'),
    description: t('immutable-storage.key-benefits.item-1.description'),
  },
  {
    title: t('immutable-storage.key-benefits.item-2.title'),
    description: t('immutable-storage.key-benefits.item-2.description'),
  },
  {
    title: t('immutable-storage.key-benefits.item-3.title'),
    description: t('immutable-storage.key-benefits.item-3.description'),
  },
  {
    title: t('immutable-storage.key-benefits.item-4.title'),
    description: t('immutable-storage.key-benefits.item-4.description'),
  },
  {
    title: t('immutable-storage.key-benefits.item-5.title'),
    description: t('immutable-storage.key-benefits.item-5.description'),
  },
  {
    title: t('immutable-storage.key-benefits.item-6.title'),
    description: t('immutable-storage.key-benefits.item-6.description'),
  },
];
const ACCORDION = [
  {
    title: t('immutable-storage.faq.item-1.title'),
    description: t('immutable-storage.faq.item-1.description'),
  },
  {
    title: t('immutable-storage.faq.item-2.title'),
    description: t('immutable-storage.faq.item-2.description'),
  },
  {
    title: t('immutable-storage.faq.item-3.title'),
    description: t('immutable-storage.faq.item-3.description'),
  },
  {
    title: t('immutable-storage.faq.item-4.title'),
    description: t('immutable-storage.faq.item-4.description'),
  },
  {
    title: t('immutable-storage.faq.item-5.title'),
    description: t('immutable-storage.faq.item-5.description'),
  },
  {
    title: t('immutable-storage.faq.item-6.title'),
    description: t('immutable-storage.faq.item-6.description'),
  },
];

const activeTabIndex = ref(0);
const setActiveTabIndex = (index: number) => {
  activeTabIndex.value = index;
};

const sliderOffset = ref(0);
const calcSliderOffset = () => {
  const SCREEN_WIDTH = window.screen.width;

  if (SCREEN_WIDTH > 1216) {
    sliderOffset.value = (SCREEN_WIDTH - 1216) / 2;
  } else if (SCREEN_WIDTH > 767) {
    sliderOffset.value = 16 * 2;
  } else {
    sliderOffset.value = 16;
  }
};

const { data } = await useAsyncGql('RelatedGuides');
const relatedTopics = computed(() => get(data.value, 'blog_articles.data', []));

onMounted(() => {
  calcSliderOffset();
  window.addEventListener('resize', calcSliderOffset, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('resize', calcSliderOffset);
});

useApiSeo('immutable-storage');
</script>

<style scoped lang="scss">
@import '$/mixins/components';
@import '$/mixins/size';
@import '$/mixins/typography';
@import '$/mixins/flex';
@import '$/variables/shadows';
@import '$/variables/fonts';

[data-page-name='immutable-storage'] {
  padding-top: 6.25rem;
  overflow-x: hidden;

  @include tablet {
    padding-top: 5rem;
  }

  @include mobile {
    padding-top: 2.4rem;
  }

  section.hero {
    margin-bottom: 6.25rem;

    @include tablet {
      margin-bottom: 2.25rem;
    }

    @include mobile {
      margin-bottom: 1.2rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);
      @include flex-center-sb;
      gap: 0 2rem;

      @include tablet {
        flex-direction: column;
      }

      @include mobile {
        padding: 0 1rem;
      }

      .left {
        @include flex-start-start;
        flex-direction: column;

        span.tag {
          @include body-3;
          padding: 0.25rem 0.5rem;
          color: token('chip-primary-text');
          background-color: token('chip-primary-surf-cont');

          margin-bottom: 1rem;

          @include tablet {
            margin-bottom: 1.6rem;
          }

          @include mobile {
            margin-bottom: 1rem;
          }
        }

        h1 {
          @include h2;
          margin-bottom: 2rem;

          @include tablet {
            margin-bottom: 1.5rem;
          }

          @include mobile {
            margin-bottom: 1rem;
          }
        }

        p {
          @include body-3;
          margin-bottom: 2rem;

          @include tablet {
            @include body-1;
            max-width: 100%;
            margin-bottom: 1.5rem;
          }

          @include mobile {
            margin-bottom: 2.5rem;
          }
        }

        .buttons {
          @include flex-center-sb;
          gap: 0 2rem;

          @include mobile {
            flex-direction: column;
            width: 100%;
            gap: 1rem 0;
          }

          [data-component-name='Buttons.Link'],
          [data-component-name='Buttons.Regular'] {
            @include mobile {
              width: 100%;
            }
          }

          [data-component-name='Buttons.Link'] {
          }

          [data-component-name='Buttons.Regular'] {
            svg {
              fill: token('icon-primary');
            }
          }
        }
      }

      .right {
        width: 100%;

        @include mobile {
          width: 100%;
        }

        img {
          @include tablet {
            width: 100%;
            max-width: 37rem;
            margin: 0.15rem auto 0 auto;
            display: block;
          }

          @include mobile {
            width: 100%;
            max-width: 20.5rem;
            transform: scale(1.07) translate(-0.05rem, 0.1rem);
          }
        }
      }
    }
  }

  .purple-1 {
    margin-bottom: 6.25rem;

    @include tablet {
      margin-bottom: 3.5rem;
    }

    @include mobile {
      margin-bottom: 4rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      .content {
        padding: 2rem;
        background-color: token('surf-cont-secon-action');

        @include mobile {
          padding: 1.5rem 1rem;
        }

        &:deep(p) {
          color: token('text-reversed');
          @include subtitle-3;

          &:first-of-type {
            margin-bottom: 0.5rem;
          }

          a {
            color: token('link-reversed');
            text-decoration: underline;

            &:hover {
              background-color: rgba(token('link-reversed'), 16%);
            }
          }
        }
      }
    }
  }

  .tabs {
    margin-bottom: 6.25rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    @include mobile {
      margin-bottom: 4rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      [data-component-name='TabList'] {
        button {
          height: 100%;
          width: 100%;
          padding: 1rem 0;

          @include subtitle-3;

          color: token('text-primary');
          transition: color 0.15s ease;

          &.active {
            color: token('text-action');
          }
        }
      }

      ul.contents {
        &::v-deep(li.tab-content) {
          margin: 5rem auto 0 auto;
          @include fluid-max-width(50rem);

          @include tablet {
            margin-top: 3rem;
          }

          @include mobile {
            margin-top: 1.5rem;
          }

          strong,
          p {
            @include body-3;
            margin-bottom: 0.5rem;

            a {
              color: token('link');
              text-decoration: underline;

              &:hover {
                background-color: rgba(token('link'), 16%);
              }
            }
          }

          ul {
            margin-left: 2.4rem;

            li {
              position: relative;

              &:before {
                content: '';
                display: block;

                @include fixed-size(7px);
                background-color: token('icon-primary');
                border-radius: 100px;

                position: absolute;
                left: -1.5rem;
                top: 0.9rem;
              }

              strong {
                font-weight: 700;
              }

              p {
                display: inline;
              }
            }
          }
        }
      }
    }
  }

  section.role-of-storage {
    margin-bottom: 6.25rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    @include mobile {
      margin-bottom: 1.75rem;
    }

    @mixin card {
      @include flex-start-start;
      flex-direction: column;

      padding: 2rem;
      box-shadow: map-get($shadows, 'Elevation 1');

      @include tablet {
        padding: 1.5rem;
      }

      @include mobile {
        padding: 1.5rem 1rem;
      }

      strong {
        @include subtitle-1;
      }

      svg {
        @include fixed-size(3.125rem);
        margin: 1.5rem 0 1.5rem 0;
      }

      &:deep(p) {
        @include body-2;

        a {
          @include app-link;
          @include link-text-2;
        }
      }
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      h2 {
        @include h3;
        margin-bottom: 2rem;
        max-width: 30ch;

        @include tablet {
          margin-bottom: 1.5rem;
          max-width: 100%;
        }

        @include mobile {
          margin-bottom: 1.5rem;
        }
      }

      &:deep(p) {
        @include body-3;
        @include fluid-max-width(57rem);

        a {
          @include app-link;
        }
      }

      h4 {
        @include h4;
        margin: 5rem 0 2rem;

        @include tablet {
          margin-bottom: 2.5rem;
        }

        @include mobile {
          margin: 4rem 0 0 0;
        }
      }

      ul.cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: auto;
        grid-gap: 2rem;

        margin-top: 2rem;

        @include tablet {
          grid-template-columns: repeat(2, 1fr);
          grid-gap: 1.5rem;
          margin-top: 2.5rem;
        }

        @include mobile {
          display: none;
        }

        li.card {
          @include card;
        }
      }
    }

    .swiper {
      display: none;

      @include mobile {
        display: block;

        .card {
          @include card;
          max-width: 18rem;
          height: auto;
        }

        &:deep(.swiper-wrapper) {
          padding: 1.5rem 0 5rem 0;
          box-shadow: none;
        }

        &:deep(.swiper-pagination) {
          @include swiper-pagination;
          transform: translateY(-1.75rem);
        }
      }
    }
  }

  section.purple-whitepaper {
    margin-bottom: 6.4rem;

    @include mobile {
      margin-bottom: 4rem;
    }

    .wrapper {
      padding: 2rem 2rem 2.6rem 2rem;
      @include wrapper(80rem);

      @include flex-start-sb;
      gap: 0 2rem;

      @include tablet {
        padding: 4rem 2rem 4.9rem 2rem;
      }

      @include mobile {
        @include flex-center-sb;
        flex-direction: column;
        padding: 2.5rem 1rem;
      }

      .left {
        @include flex-start-sb;
        flex-direction: column;
        align-self: center;
        width: 75%;

        @include tablet {
          width: 90%;
        }

        @include mobile {
          width: 100%;
          max-width: 100%;
        }

        &:deep(h2) {
          @include h4;
          color: token('text-reversed');
          margin: 0.7rem 0 2rem 0;

          @include tablet {
            margin: 0.2rem 0 2.5rem 0;
            line-height: 1.8rem;
          }

          @include mobile {
            line-height: 1.5rem;
            margin: 0 0 1.5rem 0;
          }

          span.veeam {
            @include veeam;
            line-height: 2.2rem;

            @include tablet {
              line-height: 2.1rem;
            }

            @include mobile {
              line-height: 1.3rem;
            }
          }
        }

        [data-component-name='Buttons.Regular'] {
          @include mobile {
            width: 100%;
          }
        }
      }

      .right {
        align-content: center;

        @include tablet {
          width: 50%;
        }

        @include mobile {
          display: none;
        }

        img {
          transform: translate(-0.5rem, 0.5rem);

          @include tablet {
            transform: translate(2.8rem, 1rem);
          }

          @include mobile {
          }
        }
      }
    }
  }

  section.key-benefits {
    margin-bottom: 6.25rem;

    @include tablet {
      margin-bottom: 2.15rem;
    }

    @include mobile {
      margin-bottom: 1.5rem;
    }

    @mixin card {
      padding: 2rem;
      border-top: 1px solid token('outline-primary');

      @include tablet {
        padding: 1.5rem;
      }

      @include mobile {
        padding: 1.5rem 1rem;
      }

      strong {
        @include subtitle-1;

        display: block;
        margin-bottom: 2rem;

        @include tablet {
          margin-bottom: 1rem;
        }

        @include mobile {
          margin-bottom: 1rem;
        }
      }

      &::v-deep(p) {
        @include body-3;

        @include tablet {
          font-family: map-get($fonts, 'Inter');
          font-size: 1.25rem;
          font-style: normal;
          font-weight: 400;
          line-height: 1.875rem; /* 150% */
        }

        @include mobile {
          font-family: map-get($fonts, 'Inter');
          font-size: 1rem;
          font-style: normal;
          font-weight: 400;
          line-height: 1.5rem; /* 150% */
        }

        a {
          @include app-link;
          font-weight: normal;

          @include tablet {
            font-size: inherit;
          }
        }
      }
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      .intro {
        @include fluid-max-width(57rem);
        margin-bottom: 4.9rem;

        @include tablet {
          margin-bottom: 3rem;
        }

        @include mobile {
          margin-bottom: 0;
        }

        &:deep(h2) {
          @include h3;
          margin-bottom: 2rem;

          @include tablet {
            margin-bottom: 1.5rem;
          }

          br {
            display: none;

            @include tablet {
              display: initial;
            }
          }
        }

        p.description {
          @include body-3;

          &:not(:last-of-type) {
            margin-bottom: 0.5rem;
          }
        }
      }

      ul.list {
        @include tablet {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: 0.35rem 1.5rem;
        }

        @include mobile {
          display: none;
        }

        li.item {
          @include card;
        }
      }
    }

    .swiper {
      display: none;

      @include mobile {
        display: block;

        .card {
          @include card;
          max-width: 18rem;
          min-height: 18.5rem;
        }

        &:deep(.swiper-wrapper) {
          padding: 2.5rem 0 5.2rem 0;
        }

        &:deep(.swiper-pagination) {
          @include swiper-pagination;
          transform: translateY(-3rem);
        }
      }
    }
  }

  section.best-immutable-storage {
    margin-bottom: 5rem;

    @include tablet {
      margin-bottom: 2.5rem;
    }

    @include mobile {
      margin-bottom: 4rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      display: grid;
      grid-gap: 2rem 0;
      grid-template-areas:
        'title       image'
        'description image'
        'button      image'
        '.           image';

      @include tablet {
        grid-gap: 1.5rem 0;
        grid-template-areas:
          'title'
          'description'
          'button'
          'image';
      }

      @include mobile {
        padding: 0 1rem;
        grid-gap: 1rem 0;
        grid-template-areas:
          'title'
          'description'
          'image'
          'button';
      }

      &:deep(h2) {
        grid-area: title;
        @include h3;
        margin-top: 2.3rem;

        @include mobile {
          margin-top: 0;
        }

        span.veeam {
          @include veeam;
        }
      }

      &:deep(p) {
        grid-area: description;
        @include body-3;
        @include fluid-max-width(37rem);

        @include tablet {
          @include fluid-max-width(100%);
        }

        a {
          @include app-link;
        }
      }

      [data-component-name='Buttons.Link'] {
        grid-area: button;
        width: max-content;

        @include mobile {
          width: 100%;
        }
      }

      img {
        grid-area: image;

        @include tablet {
          margin: -1.55rem auto 0 auto;
          @include fluid-max-width(37rem);
        }

        @include mobile {
          margin: 0 auto 0.5rem auto;
          transform: scale(1.03) translate(0, 0.2rem);

          max-width: 37rem;
          width: 100%;
          height: auto;
        }
      }
    }
  }

  .purple-2 {
    margin-bottom: 6.4rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    @include mobile {
      margin-bottom: 4rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      .content {
        padding: 2rem;
        background-color: token('surf-cont-secon-action');

        &:deep(p) {
          color: token('text-reversed');
          @include subtitle-1;

          a {
            @include app-link('link-reversed');
            font-size: inherit;
          }
        }
      }
    }
  }

  section.related-topics {
    margin-bottom: 6.25rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    @include mobile {
      margin-bottom: 4rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      h2 {
        @include h3;
        margin-bottom: 2.5rem;

        @include tablet {
          margin-bottom: 1.5rem;
        }

        @include mobile {
          margin-bottom: 1rem;
        }
      }

      ul.list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(3, auto);
        grid-auto-flow: column;
        grid-gap: 0 2rem;

        @include tablet {
          grid-template-columns: repeat(1, 1fr);
          grid-auto-flow: row;
          grid-gap: 0.65rem 0;
        }

        li.item {
          margin-top: 2rem;

          @include tablet {
            margin-top: 1.5rem;
          }

          @include mobile {
            margin-top: 1rem;
          }

          &:nth-child(1),
          &:nth-child(4) {
            margin-top: 0;

            @include tablet {
              margin-top: 1.5rem;
            }
          }
        }
      }
    }
  }

  section.faq {
    margin-bottom: 6.1rem;

    @include tablet {
      margin-bottom: 4.85rem;
    }

    @include mobile {
      margin-bottom: 3.8rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include flex-start-sb;
      gap: 0 2rem;

      @include tablet {
        flex-direction: column;
        gap: 3rem 0;
      }

      @include mobile {
        padding: 0 1rem;
        gap: 2rem 0;
      }

      .left {
        h2 {
          @include h3;
        }
      }

      .right {
        @include fluid-max-width(56.125rem);

        @include tablet {
          max-width: 100%;
        }

        &:deep(.accordion-list) {
          .accordion-item {
            margin-top: 0;
            border-radius: unset;
            background-color: transparent;

            border: unset;
            position: relative;

            &:before {
              content: '';
              display: block;

              position: absolute;
              top: 0;
              left: 0;
              right: 0;

              height: 1px;
              width: 100%;

              transition: all 0.4s ease;
              box-shadow: 0 0 0 1px token('outline-secondary');
            }

            &--open,
            &:hover {
              &:before {
                box-shadow: 0 0 0 2px token('outline-action');
              }
            }

            &--open {
            }

            &__summary {
              background-color: unset;
              border-bottom: unset;
              padding-left: 0;
              padding-right: 2.05rem;

              @include mobile {
                padding-right: 1rem;
              }

              &-title {
                h5 {
                  @include subtitle-1;
                  color: token('text-primary');

                  margin: 0.7rem 0;

                  @include mobile {
                    margin: 0.35rem 0;
                  }
                }
              }

              &-icon {
                svg {
                }
              }
            }

            &__content {
              padding: 0;
              margin-right: 2rem;

              @include mobile {
                margin-right: 0;
              }

              p {
                @include body-2;
                margin-bottom: 2rem;

                a {
                  color: token('link');
                  text-decoration: underline;

                  &:hover {
                    background-color: rgba(token('link'), 16%);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  section.purple-3 {
    margin-bottom: 6.25rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    @include mobile {
      margin-bottom: 4rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      .content {
        @include flex-center-center;
        flex-direction: column;
        gap: 2rem 0;

        padding: 2rem;
        background-color: token('surf-cont-secon-action');

        @include tablet {
          gap: 2.5rem 0;
        }

        @include mobile {
          gap: 1rem 0;
          padding: 1.5rem 1rem;
        }

        &:deep(h3) {
          color: token('text-reversed');
          @include h4;
          text-align: center;
          transform: translateX(0.5rem);

          @include tablet {
            @include h3;
            transform: translate(0, 0.2rem);
          }

          @include mobile {
            @include h4;
            transform: translate(0, 0);
          }

          span.veeam {
            @include veeam;
            line-height: 2.1rem;

            @include mobile {
              line-height: 1.3rem;
            }
          }
        }

        [data-component-name='Buttons.Link'] {
          width: fit-content;

          @include mobile {
            width: 100%;
          }
        }
      }
    }
  }

  section.additional-info {
    @include wrapper(80rem);
    margin-bottom: 6.25rem;
    padding: 0 2rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    @include mobile {
      margin-bottom: 3.9rem;
      padding: 0 1rem;
    }

    h2 {
      @include subtitle-1;
      margin-bottom: 1.5rem;
    }

    p {
      @include subtitle-italic-2;

      :deep(a) {
        @include app-link;
        @include link-text-2;
        word-break: break-all;
        font-style: normal;
      }
    }
  }
}
</style>
